import React, { useRef, useState } from 'react';
import { ConfirmDialog } from '@/components/ui';
import { ConfirmDialogContext } from '@/hooks';
function isPromiseLike(value) {
  return value != null && typeof value.then === 'function';
}
const EMPTY_DIALOG_PROPS = {
  variant: 'confirm',
  title: undefined,
  content: undefined,
  onConfirm: undefined,
  onCancel: undefined,
  onClickOutside: undefined,
  color: undefined,
  confirmButtonText: undefined,
  cancelButtonText: undefined,
  maxWidth: undefined,
  icon: undefined,
  pattern: undefined
};
export const ConfirmDialogProvider = ({
  children
}) => {
  const [isOpen, setIsOpen] = useState(false);
  /** Keeps the dialog rendered while closing so the exit transition can play; unmounted on `onExited`. */
  const [isMounted, setIsMounted] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [dialogProps, setDialogProps] = useState(EMPTY_DIALOG_PROPS);
  /** Bumps when a new dialog opens so finishClose does not dismiss a replacement confirm. */
  const dialogGenerationRef = useRef(0);
  const dialogPropsRef = useRef(dialogProps);
  dialogPropsRef.current = dialogProps;
  const isOpenRef = useRef(isOpen);
  isOpenRef.current = isOpen;
  const confirm = options => {
    dialogGenerationRef.current += 1;
    setPendingAction(null);
    // Replace (don't merge) so optional fields like confirmButtonText don't leak from a prior dialog.
    setDialogProps({
      ...EMPTY_DIALOG_PROPS,
      ...options,
      variant: options.variant ?? 'confirm'
    });
    setIsMounted(true);
    setIsOpen(true);
  };
  const finishClose = () => {
    setIsOpen(false);
    setPendingAction(null);
    // Unmount + prop reset happen in handleExited so the exit transition plays with the current content.
  };
  const handleExited = () => {
    // A new confirm() may have interrupted the exit; don't unmount the replacement dialog.
    if (isOpenRef.current) return;
    setIsMounted(false);
    setDialogProps(EMPTY_DIALOG_PROPS);
  };
  const finishCloseIfCurrent = generation => {
    if (dialogGenerationRef.current === generation) {
      finishClose();
    }
  };
  const clearPendingIfCurrent = generation => {
    if (dialogGenerationRef.current === generation) {
      setPendingAction(null);
    }
  };
  const handleConfirm = () => {
    const generation = dialogGenerationRef.current;
    const fn = dialogPropsRef.current.onConfirm;
    if (!fn) {
      finishClose();
      return;
    }
    const result = fn();
    if (isPromiseLike(result)) {
      setPendingAction('confirm');
      void result.then(() => finishCloseIfCurrent(generation)).catch(() => clearPendingIfCurrent(generation));
    } else {
      finishCloseIfCurrent(generation);
    }
  };
  const handleCancel = () => {
    const generation = dialogGenerationRef.current;
    const fn = dialogPropsRef.current.onCancel;
    if (!fn) {
      finishClose();
      return;
    }
    const result = fn();
    if (isPromiseLike(result)) {
      setPendingAction('cancel');
      void result.then(() => finishCloseIfCurrent(generation)).catch(() => clearPendingIfCurrent(generation));
    } else {
      finishCloseIfCurrent(generation);
    }
  };
  const handleClickOutside = () => {
    const generation = dialogGenerationRef.current;
    const fn = dialogPropsRef.current.onClickOutside;
    if (!fn) {
      finishClose();
      return;
    }
    const result = fn();
    if (isPromiseLike(result)) {
      setPendingAction('cancel');
      void result.then(() => finishCloseIfCurrent(generation)).catch(() => clearPendingIfCurrent(generation));
    } else {
      finishCloseIfCurrent(generation);
    }
  };
  const dialogElement = isMounted ? <ConfirmDialog open={isOpen} {...dialogProps} pendingAction={pendingAction} onConfirm={handleConfirm} onCancel={handleCancel} onClickOutside={dialogProps.onClickOutside ? handleClickOutside : undefined} onExited={handleExited} /> : null;
  return <ConfirmDialogContext.Provider value={{
    confirm
  }}>
            {children}

            {/* Render the dialog automatically */}
            {dialogElement}
        </ConfirmDialogContext.Provider>;
};